/**
 * ธีมสว่าง / มืด
 *
 * ────────────────────────────────────────────────────────────────
 *  สามสถานะ ไม่ใช่สอง
 * ────────────────────────────────────────────────────────────────
 *
 *   'system'  ตามค่าของเครื่อง — ค่าเริ่มต้น และเปลี่ยนตามทันทีถ้าผู้ใช้ไปสลับที่ระบบ
 *   'light'   ผู้ใช้เลือกสว่างเอง ทับค่าของเครื่อง
 *   'dark'    ผู้ใช้เลือกมืดเอง ทับค่าของเครื่อง
 *
 * ⚠ ต้องมี 'system' อย่าตัดเหลือสองสถานะ
 * ถ้าเก็บแค่ light/dark ผู้ใช้ที่ตั้งเครื่องไว้ให้สลับตามเวลาจะถูกล็อกไว้ที่ธีมเดียว
 * ตั้งแต่วินาทีที่เผลอกดปุ่มครั้งแรก และไม่มีทางกลับไปโหมด "ตามเครื่อง" ได้อีก
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมต้องเขียน data-theme ลง <html> ไม่ใช่ปล่อยให้ CSS จัดการเอง
 * ────────────────────────────────────────────────────────────────
 *
 * styles.css เลือกโหมดมืดด้วย :root[data-theme="dark"] อย่างเดียว ไม่มีบล็อก
 * @media (prefers-color-scheme) แล้ว เพราะสองทางเลือกนั้นต้องเขียนโทเคนสีทั้งชุดซ้ำกัน
 * และสำเนาที่สองคือสำเนาที่แก้ตามกันไม่ทัน
 *
 * ที่นี่จึงเป็นที่เดียวในระบบที่แปล "ความชอบ" เป็น "แอตทริบิวต์"
 * และมีสคริปต์สั้น ๆ ใน index.html ทำงานเดียวกันก่อนหน้าจอวาดครั้งแรก
 * เพื่อไม่ให้ผู้ใช้โหมดมืดเห็นหน้าขาวแวบหนึ่งตอนโหลด — ⚠ ถ้าแก้ตรรกะที่นี่
 * ต้องแก้สคริปต์ใน index.html ให้ตรงกันด้วย ไม่งั้นจอจะกะพริบเปลี่ยนสีตอนโหลด
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

/** ⚠ ต้องตรงกับคีย์ในสคริปต์ของ index.html */
const KEY = 'yana.theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark';
}

/**
 * localStorage โยน error ได้ทั้งตอนอ่านและเขียน ในโหมดส่วนตัวของบางเบราว์เซอร์
 * และในเครื่องที่ตั้งค่าห้ามเว็บเก็บข้อมูล — ธีมไม่ใช่เรื่องคอขาดบาดตาย
 * ห้ามปล่อยให้ทั้งแอปพังเพราะอ่านค่านี้ไม่ได้
 */
export function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return isChoice(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

/** ธีมที่แสดงจริงหลังแปล 'system' เป็นค่าของเครื่องแล้ว */
export function resolve(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function applyChoice(choice: ThemeChoice): void {
  document.documentElement.dataset.theme = resolve(choice);
  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* เขียนไม่ได้ก็แค่จำข้ามรอบไม่ได้ หน้าจอรอบนี้ยังถูกต้อง */
  }
}

/**
 * ฟังค่าของเครื่อง — ทำงานเฉพาะตอนอยู่โหมด 'system'
 * คืนฟังก์ชันเลิกฟัง
 */
export function watchSystem(onChange: () => void): () => void {
  try {
    const mq = window.matchMedia(DARK_QUERY);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  } catch {
    return () => {};
  }
}
